import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Accordion, Input, Icon, Section, Subsection, Message, TextAreaLabel, ToggleContainer, InputLabel, MiniTab, SelectLabel } from '@weezevent/nacre';

import TransitionActionComponent, { ALLOWED_ACTIONS } from './ActionsHandler';

import Styles from '../transitionDetails.module.css';

/**
 * Transition action main block
 * @param {Object} action - Action object
 * @param {Array} actions - Form actions
 * @param {Array} transitionActions - Existing transition actions
 * @param {Boolean} loadingTransitionActions - Existing transition actions loading state
 * @param {Function} handleAction - Action properties setter
 * @param {Function} handleActionsList - Actions list increment/decrement
 * @returns {React.Component} Transition action accordion
 */
const TransitionActionCard = ({ action, actions, index, handleAction, handleActionsList, loadingTransitionActions, transitionActions }) => {
    const { t } = useTranslation();

    const actionOptions = ALLOWED_ACTIONS.filter(Boolean).map(act => ({
        text: t(`ticket.moderation.statusesTransitions.transition.steps.actions.${act}`),
        value: act
    }));

    const filteredActions = React.useMemo(() => {
        if (loadingTransitionActions || !transitionActions) {
            return [];
        }

        const _actions = transitionActions.filter(act => !actions.map(ac => ac.id).includes(act.id) || act.id === action.id);

        if (action.id) {
            return _actions.filter(act => act.object.type === action.type).map(act => act.toOption());
        }
        return _actions.filter(act => act.object.type === action.type).map(act => act.toOption());
    }, [transitionActions, loadingTransitionActions, action]);

    return (
        <Accordion
            openByDefault
            title={
                <div className={Styles['action-card']}>
                    <span>{`${t('ticket.moderation.statusesTransitions.transition.steps.actions.cardTitle')} ${index + 1}`}</span>
                    <Icon name="trash" onClick={() => handleActionsList(index)} />
                </div>
            }
        >
            <SelectLabel
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.actionSelectLabel')}
                useNew
                value={action.type}
                options={actionOptions}
                placeholder={t('ticket.common.action.select')}
                onChange={value => handleAction('type', value, index)}
                disabled={Boolean(action.id)}
            />
            <TransitionActionComponent action={action} index={index} handleAction={handleAction} options={filteredActions} />
        </Accordion>
    );
};

export const TransitionActionCardContent = ({ action, index, handleAction, options, children }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState(action.id ? 'existing' : 'new');

    return (
        <>
            {!!options?.length && (
                <MiniTab
                    activeTab={activeTab}
                    className={Styles['actions-minitab']}
                    onTabSelected={({ value }) => {
                        setActiveTab(value);
                        handleAction('type', action.type, index);
                        if (value === 'new') {
                            handleAction('id', null, index);
                        }
                    }}
                    panes={[
                        {
                            label: t('ticket.moderation.statusesTransitions.transition.steps.actions.newActionTab'),
                            value: 'new'
                        },
                        {
                            label: t('ticket.moderation.statusesTransitions.transition.steps.actions.existingActionTab'),
                            value: 'existing'
                        }
                    ]}
                />
            )}
            {activeTab === 'existing' && (
                <SelectLabel
                    label={t('ticket.moderation.statusesTransitions.transition.steps.actions.actionSelectLabel')}
                    options={options}
                    placeholder={t('ticket.common.action.select')}
                    onChange={(_, { item }) => handleAction('item', item, index)}
                    value={action.id}
                    disabled={!options?.length}
                />
            )}
            {(activeTab === 'new' || (activeTab === 'existing' && action.id)) && (
                <Section>
                    <Subsection>
                        <Message title={t('ticket.moderation.statusesTransitions.transition.steps.actions.existingActionTitle')} type="warning" visible={activeTab === 'existing'}>
                            {t('ticket.moderation.statusesTransitions.transition.steps.actions.existingActionMessage')}
                        </Message>
                        <div className={Styles['action-card-input']}>
                            <InputLabel label={t('ticket.moderation.statusesTransitions.transition.steps.actions.nameLabel')} />
                            <Input
                                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.nameLabel')}
                                onChange={({ target: { value } }) => handleAction('name', value, index)}
                                value={action.name}
                            />
                        </div>
                        <div className={Styles['action-card-area']}>
                            <TextAreaLabel
                                label={
                                    <>
                                        {t('ticket.moderation.statusesTransitions.transition.steps.actions.descriptionLabel')}
                                        <span className={Styles['label-optional']}>{t('ticket.common.labelOptionalParenthesis')}</span>
                                    </>
                                }
                                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.descriptionLabel')}
                                onChange={({ target: { value } }) => handleAction('description', value, index)}
                                value={action.description}
                            />
                        </div>

                        {/* Action's specific fields */}
                        {children}
                        {/* Action's specific fields */}

                        <ToggleContainer
                            title={t('ticket.moderation.statusesTransitions.transition.steps.actions.mandatoryActionLabel')}
                            subtitle={t('ticket.moderation.statusesTransitions.transition.steps.actions.mandatoryActionDesc')}
                            onChange={({ target: { checked } }) => handleAction('optional_value', checked, index)}
                            value={action.optional_value}
                            toggle={action.optional_value}
                        />
                    </Subsection>
                </Section>
            )}
        </>
    );
};

TransitionActionCard.propTypes = {
    action: PropTypes.object.isRequired,
    actions: PropTypes.array,
    index: PropTypes.number.isRequired,
    handleAction: PropTypes.func.isRequired,
    handleActionsList: PropTypes.func.isRequired,
    loadingTransitionActions: PropTypes.bool,
    transitionActions: PropTypes.array
};

TransitionActionCardContent.propTypes = {
    action: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    index: PropTypes.number.isRequired,
    handleAction: PropTypes.func.isRequired,
    options: PropTypes.array
};

TransitionActionCard.defaultProps = {
    actions: [],
    transitionActions: []
};

TransitionActionCardContent.defaultProps = {
    options: []
};

export default TransitionActionCard;
